/**
 * Intl-backed formatters (DEN-99 §6), driven by an explicit `FormatConfig`
 * rather than the active UI locale:
 *
 *  - `date`      date-only ISO values, pinned to UTC, reassembled from
 *                `formatToParts` so day/month order follows `datePattern`.
 *  - `datetime`  a true instant: the date part in the viewer's local zone plus
 *                `HH:mm` (24h unless `hour12`).
 *  - `time`      a clinic wall time `HH:MM` (never an instant): 24h, or 12h
 *                with AM/PM when `hour12`; no zone is involved.
 *  - `number`    grouping only, from `separatorLocale`.
 *  - `currency`  `separatorLocale` glyphs + the fixed `currency` denomination.
 *
 * Two call routes share the memoised `Intl` instances: `{{v, date}}` inside
 * catalog strings (registered on the i18next instance) and `useFormatters()`
 * for bare values in JSX.
 */
import { createContext, useContext, useMemo } from 'react';
import type { i18n as I18nType } from 'i18next';
import type { Clock, DateFormat } from '../../shared/preferences';
import { exposedConfig } from '../generated-config';

export interface FormatConfig {
  datePattern: DateFormat;
  hour12: boolean;
  currency: string;
  separatorLocale: string;
}

type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  // A bare `YYYY-MM-DD` parses as UTC midnight already; anything with a time
  // keeps its offset.
  return new Date(value);
}

const ORDER: Record<FormatConfig['datePattern'], Array<'day' | 'month' | 'year'>> =
  {
    DMY: ['day', 'month', 'year'],
    MDY: ['month', 'day', 'year'],
    YMD: ['year', 'month', 'day'],
  };

function reassemble(
  parts: Intl.DateTimeFormatPart[],
  pattern: FormatConfig['datePattern'],
): string {
  const by = new Map(parts.map((p) => [p.type, p.value]));
  return ORDER[pattern].map((t) => by.get(t) ?? '').join('/');
}

export interface Formatters {
  date: (value: DateInput) => string;
  datetime: (value: DateInput) => string;
  time: (value: string) => string;
  number: (value: number) => string;
  currency: (value: number) => string;
}

function wallTime(value: string, hour12: boolean): string {
  if (!hour12) return value;
  const [h, m] = value.split(':').map(Number);
  const hour = h % 24; // `24:00` (the end of the day) reads as midnight
  return `${hour % 12 || 12}:${String(m).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

export function createFormatters(cfg: FormatConfig): Formatters {
  // `en-GB` is only a digit/2-digit source; order comes from `reassemble`.
  const dateUtc = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const dateLocal = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: cfg.hour12,
    ...(cfg.hour12 ? {} : { hourCycle: 'h23' as const }),
  });
  const num = new Intl.NumberFormat(cfg.separatorLocale);
  const money = new Intl.NumberFormat(cfg.separatorLocale, {
    style: 'currency',
    currency: cfg.currency,
  });

  return {
    date: (value) =>
      reassemble(dateUtc.formatToParts(toDate(value)), cfg.datePattern),
    datetime: (value) => {
      const d = toDate(value);
      return `${reassemble(dateLocal.formatToParts(d), cfg.datePattern)} ${time.format(d)}`;
    },
    time: (value) => wallTime(value, cfg.hour12),
    number: (value) => num.format(value),
    currency: (value) => money.format(value),
  };
}

/** A signed-in account's display preferences (the `date_format` / `clock` columns). */
export interface FormatPrefs {
  dateFormat: DateFormat;
  clock: Clock;
}

export interface FormatPrefsValue {
  prefs: FormatPrefs | null;
  /** Null returns to the deploy-config defaults. */
  setPrefs: (prefs: FormatPrefs | null) => void;
}

/** Provided by `I18nProvider`; an area's layout sets it from the signed-in account. */
export const FormatPrefsContext = createContext<FormatPrefsValue>({
  prefs: null,
  setPrefs: () => {},
});

/** Deploy-config defaults, with the account's date order and clock laid over them. */
export function buildFormatConfig(prefs: FormatPrefs | null): FormatConfig {
  return {
    datePattern: prefs?.dateFormat ?? exposedConfig.i18n.datePattern,
    hour12: prefs ? prefs.clock === '12h' : exposedConfig.i18n.hour12,
    currency: exposedConfig.i18n.currency,
    separatorLocale: exposedConfig.i18n.separatorLocale,
  };
}

export function useFormatPrefs(): FormatPrefsValue {
  return useContext(FormatPrefsContext);
}

export function useFormatConfig(): FormatConfig {
  const { prefs } = useContext(FormatPrefsContext);
  return useMemo(() => buildFormatConfig(prefs), [prefs]);
}

/** Bare-value formatters for table cells with no surrounding sentence. */
export function useFormatters(): {
  fmtDate: Formatters['date'];
  fmtDateTime: Formatters['datetime'];
  fmtTime: Formatters['time'];
  fmtNumber: Formatters['number'];
  fmtCurrency: Formatters['currency'];
} {
  const cfg = useFormatConfig();
  return useMemo(() => {
    const f = createFormatters(cfg);
    return {
      fmtDate: f.date,
      fmtDateTime: f.datetime,
      fmtTime: f.time,
      fmtNumber: f.number,
      fmtCurrency: f.currency,
    };
  }, [cfg]);
}

/** (Re)register the four formatters on an i18next instance for `{{v, date}}`. */
export function registerFormatters(i18n: I18nType, f: Formatters): void {
  const fmt = i18n.services?.formatter;
  if (!fmt) {
    return;
  }
  fmt.add('date', (value) => f.date(value as DateInput));
  fmt.add('datetime', (value) => f.datetime(value as DateInput));
  fmt.add('time', (value) => f.time(value as string));
  fmt.add('number', (value) => f.number(value as number));
  fmt.add('currency', (value) => f.currency(value as number));
}
